import { Injectable } from '@angular/core';
import { Observable, Subject, merge, interval, filter, share, tap } from 'rxjs';
import { WebService } from './web.service';
import { RealtimeService } from './realtime.service';

// =====================================================================
// EwsDataService — Scope 6.4 Early Warning System + Bean Logic
// ---------------------------------------------------------------------
// Thin wrapper over routes/ews.js (mounted at /api/v1/ews).
// Same pattern as SwapDataService:
//   - WebService.getWebCall picks GET vs POST by whether `body` is passed
//   - every write sends ActionByGUID from localStorage('GUID')
// =====================================================================

export type BurnRange = 3 | 6 | 12;
export type SafsType = 'PCT' | 'BAGS';
export type RunType = 'EWS' | 'BEAN_LOGIC' | 'PROPOSAL';
export type NoteScope = 'EWS' | 'BEAN_LOGIC';

/** The shared EWS filter set (Summary + Group Analysis) */
export interface EwsFilters {
  AsOfDate?: string | null;          // 'YYYY-MM-DD' — normally left empty (test-mode setting / today)
  BurnRange?: BurnRange | null;
  WarehouseID?: number | null;       // null = all, 37 (incl. 43), 45, 31 — Summary only
  SafsValue?: number | null;
  SafsType?: SafsType | null;
  CropOption?: 1 | 2 | null;
  VolatileMarket?: boolean;
  ExcludeFireSales?: boolean;
  DaysInAdj?: number | null;
  GroupIDs?: number[] | null;
  ExcludeCustomers?: number[] | null;
  IncludeInactive?: boolean;         // Summary only
  TopCustomers?: number | null;      // Group Analysis only
  IncludeSKUs?: number[] | null;     // Group Analysis only — legacy ✓ / Add SKU (B2.2)
  ExcludeSKUs?: number[] | null;     // Group Analysis only — legacy ✗ (B2.2)
}

export interface EwsSummaryResult {
  Groups: any[];
  Months: any[];
  RunInfo: any;
}

export interface EwsGroupAnalysisResult {
  Header: any;
  Warehouses: any[];
  Waterfall: any[];
  Customers: any[];
  CustomerMonths: any[];
  Skus: any[];
  FreightSamples: any[];
  BurnByYear: any[];
  ForwardLots: any[];
}

export interface BeanLogicResult {
  Header: any;
  Groups: any[];
  Bars: any[];
  Skus: any[];
  RnyByWarehouse: any[];
}

export interface CustomerInputRow {
  GroupID: number;
  InventoryTypeID?: number | null;
  RoasteryBags?: number | null;
  BurnRateOverride?: number | null;
  ShowInPrint?: boolean;
  CustomerAccepted?: boolean;
}

export interface EwsNote {
  NoteID?: number | null;
  NoteScope: NoteScope;
  GroupID?: number | null;
  CompanyID?: number | null;
  InventoryTypeID?: number | null;
  WarehouseID?: number | null;
  RunID?: number | null;
  NoteType?: string | null;
  NoteText: string;
  IsCustomerVisible?: boolean;
  FollowUpDate?: string | null;      // 'YYYY-MM-DD'
}

export interface ForecastLine {
  LineType: 'MONTH' | 'SUMMARY' | 'PROPOSAL' | 'RECOMMENDATION';
  GroupID?: number | null;
  InventoryTypeID?: number | null;
  WarehouseID?: number | null;
  MonthStart?: string | null;        // 'YYYY-MM-DD'
  BurnRate?: number | null;
  GoalBags?: number | null;
  OpeningBags?: number | null;
  ArrivalsSoldBags?: number | null;
  ArrivalsUnsoldBags?: number | null;
  ReservedBags?: number | null;
  ClosingBags?: number | null;
  RecommendedBags?: number | null;
  ProposedBags?: number | null;
  LineNote?: string | null;
}

export interface ForecastRunPayload {
  RunType: RunType;
  RunName?: string | null;
  AsOfDate?: string | null;
  BurnRange?: BurnRange | null;
  WarehouseID?: number | null;
  SafsValue?: number | null;
  SafsType?: SafsType | null;
  CropOption?: 1 | 2 | null;
  VolatileMarket?: boolean;
  ExcludeFireSales?: boolean;
  DaysInAdj?: number | null;
  GroupIDs?: number[] | null;
  CompanyID?: number | null;
  IncludeSKUs?: number[] | null;
  ExcludeSKUs?: number[] | null;
  ExcludeCustomers?: number[] | null;
  Assumptions?: Record<string, any> | null;
  Notes?: string | null;
  Lines: ForecastLine[];
}

@Injectable({
  providedIn: 'root'
})
export class EwsDataService {
  private localChanges = new Subject<void>();
  readonly changes$: Observable<any>;
  constructor(private web: WebService, realtime: RealtimeService) {
    // The minute check also catches imports and changes made in legacy RMS.
    this.changes$ = merge(this.localChanges, realtime.ewsChanged$, realtime.orderFlowChanged$,
      realtime.skuPricingChanged$, interval(60000)).pipe(filter(() => !document.hidden), share());
  }

  get currentUserGUID(): string | null {
    return localStorage.getItem('GUID');
  }

  // Drops null/undefined/''/[] so the query string only carries real values.
  // Booleans → 1/0, arrays → '1,2,3'.
  private toParams(obj: Record<string, any>): { name: string, value: any }[] {
    return Object.entries(obj)
      .filter(([, v]) => v !== undefined && v !== null && v !== '' && !(Array.isArray(v) && v.length === 0))
      .map(([name, value]) => ({
        name,
        value: typeof value === 'boolean' ? (value ? 1 : 0)
          : Array.isArray(value) ? value.join(',')
            : value
      }));
  }

  private get(method: string, query?: Record<string, any>): Observable<any> {
    const params = this.toParams(query ?? {});
    return this.web.getWebCall('ews', method, { params, skipLoader: true });
  }

  private post(method: string, body: Record<string, any>): Observable<any> {
    return this.web.getWebCall('ews', method, { body: { ...body, ActionByGUID: this.currentUserGUID } })
      .pipe(tap(() => this.localChanges.next()));
  }

  getSkus(search = ''): Observable<any[]> { return this.get('skus', { Search: search }); }
  getReports(kind: string, f: EwsFilters = {}): Observable<any> { return this.get(`reports/${kind}`, f); }
  getAccess(): Observable<any[]> { return this.get('access'); }
  saveAccess(company: number, enabled: boolean): Observable<any> { return this.post('access/save', {CompanyID:company,Enabled:enabled}); }
  getPricing(company: number, warehouse: number | null): Observable<any> { return this.get('price-logic', {CompanyID:company,WarehouseID:warehouse}); }
  getPriceSessions(company: number): Observable<any[]> { return this.get('price-logic/sessions', {CompanyID:company}); }
  savePriceSession(company: number, data: any): Observable<any> { return this.post('price-logic/sessions/save',{CompanyID:company,Session:data}); }

  // ---------------------------------------------------------------------
  // EWS
  // ---------------------------------------------------------------------

  /** Filter bar data: { Groups, Warehouses, Settings } */
  getOptions(): Observable<{ Groups: any[]; Warehouses: any[]; Settings: any[] }> {
    return this.get('options');
  }

  /** EWS Summary (S1); pass WarehouseID for Spot & Transfer (S3) */
  getSummary(f: EwsFilters): Observable<EwsSummaryResult> {
    return this.get('summary', {
      AsOfDate: f.AsOfDate, BurnRange: f.BurnRange, WarehouseID: f.WarehouseID,
      SafsValue: f.SafsValue, SafsType: f.SafsType, CropOption: f.CropOption,
      VolatileMarket: f.VolatileMarket, ExcludeFireSales: f.ExcludeFireSales, DaysInAdj: f.DaysInAdj,
      GroupIDs: f.GroupIDs, ExcludeCustomers: f.ExcludeCustomers, IncludeInactive: f.IncludeInactive
    });
  }

  /** Group Analysis (S2) — one or more groups analysed together */
  getGroupAnalysis(groupIds: number[], f: EwsFilters): Observable<EwsGroupAnalysisResult> {
    return this.get('group-analysis', {
      GroupIDs: groupIds,
      AsOfDate: f.AsOfDate, BurnRange: f.BurnRange,
      SafsValue: f.SafsValue, SafsType: f.SafsType, CropOption: f.CropOption,
      VolatileMarket: f.VolatileMarket, ExcludeFireSales: f.ExcludeFireSales, DaysInAdj: f.DaysInAdj,
      ExcludeCustomers: f.ExcludeCustomers, TopCustomers: f.TopCustomers,
      IncludeSKUs: f.IncludeSKUs, ExcludeSKUs: f.ExcludeSKUs
    });
  }

  // ---------------------------------------------------------------------
  // Bean Logic
  // ---------------------------------------------------------------------

  getBeanCustomers(search?: string | null): Observable<any[]> {
    return this.get('bean-logic/customers', { Search: search });
  }

  getBeanLogic(companyId: number, opts?: { BurnRange?: BurnRange | null; WarehouseID?: number | null; BarMonths?: number | null; AsOfDate?: string | null }): Observable<BeanLogicResult> {
    return this.get(`bean-logic/${companyId}`, { ...(opts ?? {}) });
  }

  /** Save roastery bags / burn override / print / accepted — a row back at defaults is removed */
  saveCustomerInputs(companyId: number, rows: CustomerInputRow[]): Observable<{ success: boolean; RowsChanged: number; Inputs: any[] }> {
    return this.post('bean-logic/inputs/save', { CompanyID: companyId, Rows: rows });
  }

  // ---------------------------------------------------------------------
  // Notes
  // ---------------------------------------------------------------------

  getNotes(q: { NoteScope?: NoteScope; GroupID?: number | null; CompanyID?: number | null; RunID?: number | null; CustomerVisibleOnly?: boolean; IncludeDeleted?: boolean }): Observable<any[]> {
    return this.get('notes', q);
  }

  /** Add (no NoteID) or edit — an edit sends the whole note */
  saveNote(note: EwsNote): Observable<{ success: boolean; Note: any }> {
    return this.post('notes/save', { ...note });
  }

  deleteNote(noteId: number): Observable<{ success: boolean }> {
    return this.post('notes/delete', { NoteID: noteId });
  }

  // ---------------------------------------------------------------------
  // Saved forecasts
  // ---------------------------------------------------------------------

  getRuns(q?: { RunType?: RunType; CompanyID?: number | null; GroupID?: number | null; Top?: number }): Observable<any[]> {
    return this.get('runs', q);
  }

  getRun(runId: number): Observable<{ Run: any; Lines: any[] }> {
    return this.get(`runs/${runId}`);
  }

  saveRun(payload: ForecastRunPayload): Observable<{ success: boolean; RunID: number; VersionNo: number; ScopeKey: string; LineCount: number }> {
    return this.post('runs/save', { ...payload });
  }

  // ---------------------------------------------------------------------
  // Settings + audit
  // ---------------------------------------------------------------------

  /** Executive / System Admins only (the proc checks) */
  saveSetting(settingKey: string, settingValue: string | null): Observable<{ success: boolean; Setting: any }> {
    return this.post('settings/save', { SettingKey: settingKey, SettingValue: settingValue });
  }

  getAudit(q?: { FromDate?: string; ToDate?: string; EntityType?: string; CompanyID?: number; GroupID?: number; RunID?: number; Top?: number }): Observable<any[]> {
    return this.get('audit', q);
  }
}

// ---------------------------------------------------------------------
// Small helpers shared by the EWS screens
// ---------------------------------------------------------------------

/** SQL DATE comes back as '2026-10-01T00:00:00.000Z' — keep the date part only,
 *  so the date pipe shows Oct 1 (not Sep 30 in New York time). */
export function dateOnly(v: any): string | null {
  return v ? String(v).substring(0, 10) : null;
}

/** '2026-10-01…' → "Oct '26" */
export function monthLabel(v: any): string {
  const d = dateOnly(v);
  if (!d) return '';
  const names = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${names[+d.substring(5, 7) - 1]} '${d.substring(2, 4)}`;
}
